import { Vue, Options } from 'vue-class-component';
import {
  Language,
  EntrancePlantDetailDTO,
  EntrancePlantInfoCreateDTO,
  EntrancePlantStructureCreateDTO,
  ApartmentLabelDTO,
  ApartmentDTO,
  LabelType,
  BuildingImportDto,
  AddressingType,
} from '@/../client-generator/generated-clients/plants';
import i18n from '@/core/translations/i18n';
import { DenominationMode } from '@/../client-generator/generated-clients/plants';
import { setLanguageByCode, validLanguages } from '@/core/utils/utils';
import plantsClient from '@/core/services/plants';
import { CallMode, fileIsCsv, processCsvData, SimpleLabel, SingleLabelApartment } from '@/core/utils/EntrancePlant/entrance-plant.utils';
import ToggleSwitchButton from '@/views/components/ToggleSwitchButton/ToggleSwitchButton.vue';
import HelpPopupButton from '@/views/components/HelpPopupButton/HelpPopupButton.vue';
import CallModeForm from '@/views/components/CallModeForm/CallModeForm.vue';
import DialogImportFile from '@/views/pages/EntrancePlantDetail/EntrancePlantResidents/DialogImportFile/DialogImportFile.vue';
import { PropType } from 'vue';
type SelectButtonValue = {
  label: string;
  value: DenominationMode;
};

@Options({
  name: 'DialogEntrancePlantCreateStep2',
  components: {
    ToggleSwitchButton,
    HelpPopupButton,
    CallModeForm,
    DialogImportFile,
  },
  props: {
    plant: {
      type: Object as PropType<EntrancePlantDetailDTO>,
      required: true,
    },
    plantId: {
      type: Number,
      required: false,
    },
    structureCreate: {
      required: false,
      type: Object as () => EntrancePlantStructureCreateDTO,
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    plant: function (newValue) {
      if (newValue) this.initializeData();
    },
  },
  emits: ['is-loading', 'onBack', 'onNext', 'plant-edited'],
})
export default class DialogEntrancePlantCreateStep2 extends Vue {
  plant!: EntrancePlantDetailDTO;
  plantId!: number;
  structureCreate!: EntrancePlantStructureCreateDTO;
  infoPlant: EntrancePlantInfoCreateDTO = new EntrancePlantInfoCreateDTO();
  Language = Language;
  CallMode = CallMode;
  validLanguages = validLanguages;
  settingsList: SelectButtonValue[] = [
    {
      label: i18n.global.t('AppResourceEP.SetupBrfaNumbers'),
      value: DenominationMode.Numbers,
    },
    {
      label: i18n.global.t('AppResourceEP.SetupBrfaLetters'),
      value: DenominationMode.Letters,
    },
  ];
  importedData: SingleLabelApartment[] = [];
  isVisibleDialogImportFile: boolean = false;

  mounted(): void {
    this.initializeData();
  }

  initializeData() {
    const browserLanguage = navigator.language.split('-')[0];

    this.infoPlant = new EntrancePlantInfoCreateDTO({
      addressBookSorting: this.plant.addressBookSorting,
      addressingType: this.plant.addressingType,
      denominationApartments: this.plant.denominationApartments,
      denominationBuildings: this.plant.denominationBuildings,
      denominationFloors: this.plant.denominationFloors,
      denominationRisers: this.plant.denominationRisers,
      language: setLanguageByCode(browserLanguage.toLocaleLowerCase()),
      callingCodeShowAddressBook: this.plant.callingCodeShowAddressBook,
    });
  }

  handleGrouped(grouped: { [buildingKey: string]: { apartmentName: string, scsAddr: number, callingCode: string, labels: SimpleLabel[], badges:string[], keypadCode: string}[] }) {
    let dictionary = {} as { [key: string]: ApartmentDTO[] };
    for (const buildingKey in grouped) {
      const building = grouped[buildingKey];
      dictionary[buildingKey] = building.map((apartment) => {
        return new ApartmentDTO({
          name: apartment.apartmentName,
          scsAddr: apartment.scsAddr,
          code: this.isCallingCode ? apartment.callingCode : undefined,
          labels: apartment.labels.map((label) => {
            return new ApartmentLabelDTO({
              surname: label.type === LabelType.Private ? label.line1 : undefined,
              name: label.type === LabelType.Private ? label.line2 : undefined,
              companyName: label.type === LabelType.Business ? label.line1 : undefined,
              isPrivate: label.type === LabelType.Private,
              labelType: label.type,
              information: label.info,
            });
          }),
          badgeList: apartment.badges,
          keypadCode: apartment.keypadCode,
        });
      });
    }

    this.infoPlant.buildings = Object.keys(dictionary).map((key) => {
      return new BuildingImportDto({
        name: key,
        apartments: dictionary[key],
      });
    });
  }

  get isCallingCode() {
    return (
      this.infoPlant.addressingType ===
        AddressingType.DirectCall_Alphanumeric ||
      this.infoPlant.addressingType === AddressingType.DirectCall_NumbersOnly
    );
  }

  get isBRFA() {
    return (
      this.infoPlant.addressingType ===
      AddressingType.BuildingRiserFloorApartment
    );
  }

  get isBeingImported() {
    if (this.isBRFA) {
      return false;
    }
    const firstCheck = this.structureCreate.infoCreate?.buildings !== undefined && this.structureCreate.infoCreate?.buildings.length > 0;
    const secondCheck = this.plant.buildings !== undefined && this.plant.buildings.length > 0;
    return (firstCheck || secondCheck) || this.structureCreate.previousWizardPage === 'DialogEntrancePlantCreateStep3';
  }

  getDataToSave() {
    this.structureCreate.infoCreate = this.infoPlant;
    return new EntrancePlantStructureCreateDTO({
      wizardStep: 2,
      infoCreate: this.infoPlant,
    });
  }

  handleBackButton() {
    this.$emit('onBack', 'DialogEntrancePlantCreateStep1');
  }

  async handleNextButton() {
    await this.updatePlantStructure(this.getDataToSave());
    if (this.isBeingImported) {
      this.structureCreate.previousWizardPage = 'DialogEntrancePlantCreateStep2';
      this.$emit('onNext', 'DialogEntrancePlantCreateStep3');
    } else {
      this.structureCreate.previousWizardPage = 'DialogEntrancePlantCreateStep2';
      this.$emit('onNext', 'DialogEntrancePlantCreateRiserOrNot');
    }
  }

  async updatePlantStructure(structure: EntrancePlantStructureCreateDTO) {
    if (!this.plantId) return;
    this.$emit('is-loading', true);

    try {
      const result = await plantsClient.structure(this.plant.desktopId!, structure);
      this.structureCreate.infoCreate = result.infoCreate;
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    } finally {
      this.$emit('is-loading', false);
    }
  }

  triggerFilePicker() {
    const input = document.getElementById('fileInput') as HTMLInputElement;
    input.value = '';
    input.click();
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      console.error('No file selected!');
      return;
    }

    const file = input.files[0];
    if (!fileIsCsv(file)) {
      console.error('File is not a CSV!');
      return;
    }
    this.$store.dispatch('SET_APP_LOADING', true);
    const reader = new FileReader();

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      let text = e.target?.result;
      if (typeof text !== 'string') {
        console.error('Failed to read file as text!');
        this.$store.dispatch('SET_APP_LOADING', false);
        return;
      } else {
        try {
          const data = await processCsvData(text, this.infoPlant.addressingType!);
          this.importedData = data;
          this.isVisibleDialogImportFile = true;
        } catch (error) {
          console.error('Failed to parse CSV data!', error);
          const message = (error as Error).message.split('|');
          this.$toast.add({
            severity: 'error',
            summary: message[0] ?? this.$t('importFile.error'),
            details: message[1],
            closable: true,
            life: 10000,
          });
        } finally {
          this.$store.dispatch('SET_APP_LOADING', false);
        }
      }
    };

    reader.onerror = (e) => {
      console.error('Failed to read file!', e);
    };
    reader.readAsText(file); // Legge il contenuto del file come stringa
  }

  handleCloseDialog() {
    this.importedData = [];
    this.isVisibleDialogImportFile = false;
  }

  async overwrite() {
    this.handleCloseDialog();
    this.handleNextButton();
  }

  async handleUpdatePlant(plant: EntrancePlantDetailDTO) {
    this.plant = plant;
  }
}
